import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Info, RotateCcw } from "lucide-react";

const POOLS = [["EX", "Exacta"], ["TRI", "Trifecta"], ["SPR", "Superfecta"]];

const Seg = ({ options, value, onChange, testid, disabledValues = [] }) => (
  <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid={testid}>
    {options.map(([val, label]) => {
      const disabled = disabledValues.includes(val);
      const active = value === val;
      return (
        <button key={val} type="button" disabled={disabled} onClick={() => onChange(val)}
          data-testid={`${testid}-${val}`}
          className={`px-3 py-1.5 text-sm font-medium transition-colors ${active ? "bg-emerald-600 text-white" : disabled ? "bg-slate-50 text-slate-300 cursor-not-allowed" : "bg-white text-slate-700 hover:bg-slate-100"}`}>
          {label}
        </button>
      );
    })}
  </div>
);

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <Label className="text-sm text-slate-700">{label}</Label>
    <div>{children}</div>
  </div>
);

export const BetMakerPreferences = ({ open, onOpenChange, prefs, betmakerEnabled, onSave }) => {
  const [form, setForm] = useState(prefs);
  useEffect(() => { if (open) setForm(prefs); }, [open, prefs]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPoolDefault = (code, raw) => setForm((f) => {
    const next = { ...(f.poolDefaults || {}) };
    const amt = parseFloat(raw);
    if (raw === "" || isNaN(amt) || amt <= 0) delete next[code];
    else next[code] = amt;
    return { ...f, poolDefaults: next };
  });
  const togglePool = (p) => setForm((f) => {
    const has = f.pools.includes(p);
    const next = has ? f.pools.filter((x) => x !== p) : [...f.pools, p];
    return { ...f, pools: next.length ? next : f.pools };
  });
  const reset = () => setForm({
    defaultApp: "intellitix", defaultMode: "freeflow", bankroll: 50, pools: ["TRI"],
    poolDefaults: {}, oddsSource: "proj", blendPct: 0, perPool: false, roundUp: true, rememberLastUsed: false, showTopUp: true,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="prefs-modal">
        <DialogHeader>
          <DialogTitle>Default Settings</DialogTitle>
          <DialogDescription className="sr-only">Set your BetMaker defaults: app, mode, bankroll, pools, odds source, rounding, and remember-last-used.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Row label="Default App">
            <Seg testid="pref-app" value={form.defaultApp}
              disabledValues={betmakerEnabled ? [] : ["betmaker"]}
              onChange={(v) => set("defaultApp", v)}
              options={[["intellitix", "IntelliTix"], ["betmaker", "IntelliTix Pro"]]} />
          </Row>
          <Row label="Default Mode">
            <Seg testid="pref-mode" value={form.defaultMode} onChange={(v) => set("defaultMode", v)}
              options={[["freeflow", "Free Flow"], ["traditional", "Traditional"]]} />
          </Row>
          <Row label="Default Bet Amount ($)">
            <Input type="number" min="1" value={form.bankroll} data-testid="pref-bankroll"
              onChange={(e) => set("bankroll", parseFloat(e.target.value) || 0)}
              className="w-28 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
          </Row>
          <Row label="Default Pools">
            <div className="flex items-center gap-3">
              {POOLS.map(([code, name]) => (
                <label key={code} className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                  <Checkbox checked={form.pools.includes(code)} onCheckedChange={() => togglePool(code)} data-testid={`pref-pool-${code}`} />
                  {name}
                </label>
              ))}
            </div>
          </Row>
          <div className="py-2 border-b border-slate-100">
            <div className="flex items-baseline gap-1.5 mb-2 flex-wrap">
              <Label className="text-sm text-slate-700">Default bet per pool</Label>
              <span className="text-xs text-slate-400 italic">optional — blank uses the amount above</span>
            </div>
            <div className="flex flex-col gap-2 pl-1">
              {[...POOLS, ["WIN", "Win"], ["WP", "Win / Place"]].map(([code, name]) => (
                <div key={code} className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-600">{name}</span>
                  <span className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                    <Input type="number" min="1"
                      value={form.poolDefaults?.[code] ?? ""}
                      placeholder="default"
                      onChange={(e) => setPoolDefault(code, e.target.value)}
                      data-testid={`pref-pooldefault-${code}`}
                      className="w-28 pl-5 text-right placeholder:text-slate-300 placeholder:italic [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                  </span>
                </div>
              ))}
            </div>
          </div>
          <Row label="Odds Source">
            <Seg testid="pref-odds" value={form.oddsSource} onChange={(v) => set("oddsSource", v)}
              options={[["proj", "Projected"], ["live", "Live"]]} />
          </Row>
          <Row label={
            <span className="inline-flex items-center gap-1.5">
              Full Ticket Coverage
              <Popover>
                <PopoverTrigger asChild>
                  <span className="inline-flex cursor-help" data-testid="pref-rounding-info"><Info className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" /></span>
                </PopoverTrigger>
                <PopoverContent className="max-w-sm text-sm text-slate-600 leading-relaxed" data-testid="pref-rounding-popover">
                  <p className="font-semibold text-slate-800 mb-1">Ticket coverage</p>
                  <b>Full Ticket Coverage (ON):</b> every valid combination is played and your
                  bankroll is funded to match your bet amount <b>exactly</b>. The total only
                  <b>exceeds</b> your bet amount when there are too many combinations to cover them
                  all at the pool minimum.
                  <span className="block mt-2"><b>Exclude Low Probability Tickets (OFF):</b> any
                  combination whose fair (probability-weighted) stake is below the pool minimum is
                  dropped — not padded up to the minimum — and your bankroll is spread across the
                  rest so the total matches your bet amount <b>exactly</b>.</span>
                  <span className="block mt-1 text-slate-500">Example: 130 Trifecta combos × $0.50 = $65 — over a $50 bankroll. Full Coverage plays all 130 ($65); Exclude keeps the top 100 ($50).</span>
                </PopoverContent>
              </Popover>
            </span>
          }>
            <Switch checked={form.roundUp} onCheckedChange={(v) => set("roundUp", v)} data-testid="pref-roundup" />
          </Row>
          <Row label="Remember last used settings">
            <Switch checked={form.rememberLastUsed} onCheckedChange={(v) => set("rememberLastUsed", v)} data-testid="pref-remember" />
          </Row>
          <Row label="Show Complete Coverage chip (Exclude Low Probability — adds excluded combos at the pool minimum, over budget)">
            <Switch checked={!!form.showTopUp} onCheckedChange={(v) => set("showTopUp", v)} data-testid="pref-showtopup" />
          </Row>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={reset} data-testid="prefs-reset" className="gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </Button>
          <Button onClick={() => onSave(form)} data-testid="prefs-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            Save Defaults
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BetMakerPreferences;
